import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import usePopover from "src/lib/client/usePopover";
import ButtonLink from "src/components/buttonlink/ButtonLink";
import LocationResultList from 'src/components/locationresultlist/LocationResultList';
import ResultPosterRow from 'src/components/search/ResultPosterRow';
import SuspenseFallback from 'src/components/suspensefallback/SuspenseFallback';
import { useAzLocationSearch, useAzSearch } from 'src/lib/client/useSearch';
import styles from './SearchResult.module.css';
import { usePathname } from "next/navigation";
import { locale } from "@locale";
import { QUICKSEARCH_DEFAULTS, useSearchOverrides, type SearchOverrides } from "src/lib/search/searchOverrides";

//#region [Props]
type SearcResultProps = {
  searchExpression?: string;
  showSearchResult: boolean;
  azShowScore?: boolean;
  customSortNum?: number | string;
  forceHardlinks?: boolean;
};
//#endregion
const noOfMoviesToShow = 6;

//#region [Component]
export default function SearchResult(t0) {
  const $ = _c(34);
  const {
    searchExpression,
    showSearchResult,
    azShowScore: t1,
    customSortNum,
    forceHardlinks: t2
  } = t0;
  const azShowScore = t1 === undefined ? false : t1;
  const forceHardlinks = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== searchExpression) {
    t3 = searchExpression?.trim();
    $[0] = searchExpression;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = !!t3;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      active: t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    searchLoading: locationSearchLoading,
    searchResult: locationResult
  } = useAzLocationSearch(searchExpression, t5);
  const {
    overrides,
    hasOverrides,
    reset: resetOverrides
  } = useSearchOverrides();
  let t6;
  if ($[4] !== searchExpression) {
    t6 = searchExpression?.trim();
    $[4] = searchExpression;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = !!t6;
  let t8;
  if ($[6] !== customSortNum || $[7] !== overrides || $[8] !== t7) {
    t8 = {
      quickSearch: true,
      maxNumItems: noOfMoviesToShow,
      active: t7,
      customSortNum,
      overrides,
      defaults: QUICKSEARCH_DEFAULTS
    };
    $[6] = customSortNum;
    $[7] = overrides;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const {
    searchLoading: azSearchLoading,
    searchResult: azSearchResult
  } = useAzSearch(searchExpression, t8);
  const pathname = usePathname();
  let fullSearchUrl = "/sok";
  if (pathname.indexOf("/experimental") !== -1) {
    fullSearchUrl = pathname;
  }
  fullSearchUrl = `${fullSearchUrl}?q=${encodeURIComponent(searchExpression ?? "")}`;
  let t9;
  if ($[10] !== searchExpression) {
    t9 = searchExpression?.trim();
    $[10] = searchExpression;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const hasSearchExpression = !!t9;
  let t10;
  if ($[12] !== searchExpression) {
    t10 = locName => trackSearch(locName, "Kinosted", searchExpression);
    $[12] = searchExpression;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const _trackLocSearch = t10;
  let t11;
  if ($[14] !== searchExpression) {
    t11 = (id, title) => trackSearch(id, title, searchExpression);
    $[14] = searchExpression;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const _trackSearch = t11;
  const searchLoading = azSearchLoading || locationSearchLoading;
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      belowParent: true
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const popoverRef = usePopover(showSearchResult && hasSearchExpression, undefined, t12);
  let t13;
  if ($[17] !== _trackLocSearch || $[18] !== _trackSearch || $[19] !== azSearchResult || $[20] !== azShowScore || $[21] !== forceHardlinks || $[22] !== fullSearchUrl || $[23] !== hasOverrides || $[24] !== hasSearchExpression || $[25] !== locationResult?.results || $[26] !== overrides || $[27] !== resetOverrides || $[28] !== searchLoading || $[29] !== showSearchResult) {
    t13 = showSearchResult && hasSearchExpression && <>{hasOverrides && <SearchOverridesInfo overrides={overrides} onReset={resetOverrides} />}{searchLoading && <div className={styles.SSearchResultsEmpty}><SuspenseFallback text="Henter resultater..." /></div>}{!searchLoading && (azSearchResult?.results?.length ?? 0) === 0 && (locationResult?.results?.length ?? 0) === 0 && <div className={styles.SSearchResultsEmpty}>{locale.search.noresults}</div>}{!searchLoading && ((locationResult?.results?.length ?? 0) > 0 || (azSearchResult?.results?.length ?? 0) > 0) && <div className={styles.SSearchResultsList}><LocationResultList locationList={locationResult?.results} clickTracker={_trackLocSearch} forceHardlinks={forceHardlinks} />{(azSearchResult?.results?.length ?? 0) > 0 && <ResultPosterRow searchResult={azSearchResult.results} maxResults={noOfMoviesToShow} clickTracker={_trackSearch} azureSearchEnabled={true} azShowScore={azShowScore || hasOverrides} forceHardlinks={forceHardlinks} />}</div>}{!searchLoading && <div className={styles.SButtonLinkContainer}><ButtonLink className={styles.SButtonLink} href={fullSearchUrl} inverse={process.env.NEXT_PUBLIC_WEBSITE_BRAND === "filmweb"}>{locale.search.seeMore}</ButtonLink></div>}</>;
    $[17] = _trackLocSearch;
    $[18] = _trackSearch;
    $[19] = azSearchResult;
    $[20] = azShowScore;
    $[21] = forceHardlinks;
    $[22] = fullSearchUrl;
    $[23] = hasOverrides;
    $[24] = hasSearchExpression;
    $[25] = locationResult?.results;
    $[26] = overrides;
    $[27] = resetOverrides;
    $[28] = searchLoading;
    $[29] = showSearchResult;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== popoverRef || $[32] !== t13) {
    t14 = <div ref={popoverRef} popover="manual" className={styles.SSearchResult}>{t13}</div>;
    $[31] = popoverRef;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
//#endregion

//#region [Other] SearchOverridesInfo
/** Shows that the search runs with test settings (?sx.*), so it is not mistaken for the regular search */
function SearchOverridesInfo(t0) {
  const $ = _c(9);
  const {
    overrides,
    onReset
  } = t0;
  let t1;
  if ($[0] !== overrides) {
    t1 = Object.entries(overrides).map(_temp);
    $[0] = overrides;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = t1.join(", ");
  let t3;
  if ($[2] !== t2) {
    t3 = <span>Søketest: {t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onReset) {
    t4 = <button type="button" className={styles.SOverridesReset} onClick={onReset}>Nullstill</button>;
    $[4] = onReset;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className={styles.SOverrides} role="status">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
//#endregion

//#region [Other] trackSearch
function _temp(t0) {
  const [name, value] = t0;
  return `${name}=${value}`;
}
function trackSearch(id: string | number, title: string, searchExpression: string) {
  if (typeof window !== "undefined" && searchExpression?.trim().length > 0) {
    window!.dataLayer?.push({
      'event': 'fw_Quicksearch',
      'eventCategory': 'Hurtigsok',
      'eventAction': `${title} | ${id}`,
      'eventLabel': searchExpression
    });
  }
}
//#endregion